<template>
    <PlanbordGridRow :data-y="index" :data-id="resource.id" :data-rank="rankInRoster">
        <PlanbordGridLeftDataColumns>
            <ResourceNameCell
                :showExpandButton="!!showExpandButton"
                :expanded="!!expanded"
                :resource="resource"
                :externalRoster="fromOtherRoster ? externalRosterName : undefined"
                :permissions="permissionsForViewedRoster"
                :roster="roster"
                @toggle:expanded="$emit('toggle:expanded', $event)"
                @move:resourceRosterRank="$emit('move:resourceRosterRank', $event)"
                @move:rosterTitleRank="$emit('move:rosterTitleRank', $event)"
            />
            <ResourceFunctionsCell :resource="resource" />
            <ResourceCommentCell :resource="resource" />
            <ResourceStampStartDateCell :resource="resource" />
        </PlanbordGridLeftDataColumns>

        <PlanbordCell
            v-for="(day, dayIndex) in roster.days"
            :key="'ResourceDeploymentCell|' + planbordCellKey(day, dayIndex)"
            :day="day"
            :dayIndex="dayIndex"
            :resourceIndex="index"
            :wish="getWish(day)"
            :leavePeriods="getLeaveFormsOnDay(day)"
            :hasLeaveForm="!!resource.leavePeriodsFromForms && resource.leavePeriodsFromForms.some(item => isValidLeaveForm(item, day))"
            :resource="resource"
            :stamp="findStamp(day.date)"
            :ref="(el: PlanbordCell | null) => cells[Number(dayIndex)] = el"
            :roster="roster"
            :rosterNames="rosterNames"
            :organisationDeploymentStates="organisationDeploymentStates"
            :contractDeviationLabels="contractDeviationLabels"
            :shifts="shifts"
            :permissions="permissions"
            @create:deployment="$emit('create:deployment', $event)"
            @update:deployment="$emit('update:deployment', $event)"
            @delete:deployment="$emit('delete:deployment', $event)"
            @create:stampShift="$emit('create:stampShift', $event)"
        />

        <PlanbordGridRightDataColumns>
            <BalanceCompanyOrOtherRoster
                v-if="fromOtherRoster"
                :timeTypes="roster.timeTypes"
                :enabledBalanceAndCountDisplays="enabledBalanceAndCountDisplays"
                :leaveOverviews="leaveOverviews"
                :deploymentCounts="deploymentCounts"
            />
            <Balances
                v-else
                :resource="resource"
                :timeTypes="roster.timeTypes"
                :enabledBalanceAndCountDisplays="enabledBalanceAndCountDisplays"
                :leaveOverviews="leaveOverviews"
                :deploymentCounts="deploymentCounts"
            />
        </PlanbordGridRightDataColumns>
    </PlanbordGridRow>
</template>

<script lang="ts">
    import BalanceCompanyOrOtherRoster from '@/components/Planbord/Balance/BalanceCompanyOrOtherRoster.vue';
    import PlanbordGridRow from '@/components/Planbord/PlanbordGridRow.vue';
    import Balances from '@/components/Planbord/Balance/Balances.vue';
    import PlanbordCell from '@/components/Planbord/PlanbordCell.vue';
    import ResourceFunctionsCell from '@/components/ResourceFunctionsCell.vue';
    import ResourceCommentCell from '@/components/Planbord/ResourceCommentCell.vue';
    import ResourceNameCell from '@/components/Planbord/ResourceNameCell.vue';
    import {RosterDay, RosterData, PermissionsForRoster} from '@/types/types';
    import {PlanbordResource, PlanbordLeavePeriod, ResourceWithKeyedByDateObjects} from '@/types/api-object-extensions';
    import {PersonalStamp, LeavePeriodFromForm, PersonalPlanningWish, LeaveOverview, OrganisationDeploymentState, ContractCaptions, Shift, LeavePeriod} from '@cquential/api';
    import Dates from '@cquential/date-utils';
    import {BalanceFilterType} from '@/components/FilterBar/types';
    import {DeploymentCountsOfResourceOrCompany} from '@/components/types';
    import PlanbordGridLeftDataColumns from '@/components/Planbord/PlanbordGridLeftDataColumns.vue';
    import PlanbordGridRightDataColumns from '@/components/Planbord/PlanbordGridRightDataColumns.vue';
    import ResourceStampStartDateCell from '@/components/Planbord/ResourceStampStartDateCell.vue';
    import {Component, Vue, Prop} from 'vue-facing-decorator';

    @Component({
        components: {
            BalanceCompanyOrOtherRoster,
            PlanbordGridRow,
            Balances,
            PlanbordCell,
            ResourceFunctionsCell,
            ResourceCommentCell,
            ResourceNameCell,
            PlanbordGridLeftDataColumns,
            PlanbordGridRightDataColumns,
            ResourceStampStartDateCell,
        },
        emits: [
            'toggle:expanded',
            'move:resourceRosterRank',
            'move:rosterTitleRank',
            'create:deployment',
            'update:deployment',
            'delete:deployment',
            'create:stampShift',
        ],
    })
    export default class PlanbordGridRowResource extends Vue {
        @Prop({required: true}) public roster!: RosterData;
        @Prop({required: true}) public resource!: PlanbordResource | ResourceWithKeyedByDateObjects;
        @Prop({default: {}}) public rosterNames!: Record<number, string>;
        @Prop({type: Number, required: true}) public index!: number;
        @Prop({required: true}) public enabledBalanceAndCountDisplays!: Record<BalanceFilterType, Record<number, boolean>>;
        @Prop({required: true}) public leaveOverviews!: LeaveOverview[];
        @Prop({required: true}) public deploymentCounts!: DeploymentCountsOfResourceOrCompany[];
        @Prop({required: true}) public organisationDeploymentStates!: Record<number, OrganisationDeploymentState>;
        @Prop() public contractDeviationLabels?: ContractCaptions;
        @Prop({required: true}) public shifts!: Record<number, Shift>;
        @Prop({type: Boolean}) public fromOtherRoster!: boolean;
        @Prop({required: true}) public permissions!: Record<number, PermissionsForRoster>;
        @Prop({type: Boolean}) public showExpandButton!: boolean;
        @Prop({type: Boolean}) public expanded!: boolean;

        private cells: Record<number, PlanbordCell | null> = {};

        public get permissionsForViewedRoster(): PermissionsForRoster {
            return this.permissions[this.roster.rosterId];
        }

        public get rankInRoster(): number {
            return 'rankInRoster' in this.resource ? this.resource.rankInRoster : 0;
        }

        public getCell(position: number): PlanbordCell | undefined {
            return this.cells[position] ?? undefined;
        }

        public planbordCellKey(day: RosterDay, dayIndex: number): string {
            return 'index-' + dayIndex + '|day-' + day.date.getTime() + '|' + 'stamp-' + this.findStamp(day.date)?.id;
        }

        public findStamp(date: Date): PersonalStamp | undefined {
            if (this.resource.stampDeployedUntil && this.resource.stampDeployedUntil >= date) {
                return undefined;
            }

            return this.resource && this.resource.personalStamps
                ? this.resource.personalStamps.find((stamp: PersonalStamp): boolean => {
                    return stamp.beginDate <= date && (stamp.endDate === null || stamp.endDate >= date);
                })
                : undefined;
        }

        public getLeaveFormsOnDay(day: RosterDay): (PlanbordLeavePeriod | LeavePeriod)[] {
            return this.resource.leavePeriods?.filter((item: PlanbordLeavePeriod | LeavePeriod): boolean => this.isValidLeaveForm(item, day)) ?? [];
        }

        public isValidLeaveForm(form: LeavePeriodFromForm | LeavePeriod, day: RosterDay): boolean {
            return Dates.compare(day.date).isBetweenDatesInclusive(form.beginDate, form.endDate)
                && (!('state' in form) || this.hasValidState(form));
        }

        private hasValidState(form: LeavePeriodFromForm): boolean {
            return form.state !== 'rejected' && form.state !== 'denied';
        }

        public get externalRosterName(): string {
            return this.rosterNames![this.resource!.contractedTimes![0].rosterId] || 'Onbekend rooster';
        }

        public getWish(day: RosterDay): PersonalPlanningWish | undefined {
            const dayNumber: number = Dates.Utils.getDay(day.date);

            return this.resource.personalPlanningWishes!.find((item: PersonalPlanningWish): boolean => {
                return (item.recursWeekly && (item.recursOnDay % 7) === dayNumber) || !!(item.date && item.date.valueOf() === day.date.valueOf());
            });
        }
    }
</script>
